import InformationModal from './InformationModal';
import { Box } from '@mui/system';
import MediumTypography from '../../components/formlib/MediumTypography';
import { ReactComponent as DownArrow } from '../../assets/images/downarrowcolored.svg';
import { ReactComponent as UpArrow } from '../../assets/images/uparrowcolored.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/infoIcon.svg';
import { ReactComponent as UnDo } from '../../assets/images/undoIcon1.svg';
import { ReactComponent as Editsignature } from '../../assets/images/editsignature.svg';
import CustomRadioButton from '../../components/formlib/CustomRadioButton';
import IfspSignature, { Signature } from './IfspSignature';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import IfspContext from './ifspContextApi/IfspContex';
import { DateString, OptionType, SignatureStateType } from '../../utils/type';
import { AlertColor, Grid, autocompleteClasses } from '@mui/material';
import AutocompleteSearch from '../../components/formlib/AutocompleteSearch';
import DatePickerComponent from '../../components/formlib/DatePickerComponent';
import dayjs, { Dayjs } from 'dayjs';
import CheckBoxComponent from '../../components/formlib/CheckBoxComponent';
import CommentTextbox from '../../components/formlib/CommentTextbox';
import { useIntl } from 'react-intl';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import SignatureComponent from '../../components/formlib/modal/SignatureComponent';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import { shouldForceDisableOldIfsp } from '../../utils/utilities';

interface ConsentChangeServiceDeliveryPlanProps {
  close: boolean;
  canEditAfterLock: boolean;
  handelClose: Dispatch<SetStateAction<boolean>>;
  info: boolean;
  isIfspScreenLocked: boolean;
  selectedLanguage: string;
  closeInfo: (info: boolean) => void;
  openInfo: (info: boolean) => void;
  hasEditPermission: boolean;
}

interface ConsentChangeServiceDeliveryPlanDataType {
  changes: string;
  isAdminAmendment: boolean;
  isConsentSkipped: boolean;
  isEligibleUntilThirdBirthday: string;
  eligibleTool: OptionType;
  isEligibleFor: string;
  assessmentCompletionDate: DateString;
  reEstablishedDate: DateString;
  parentInitial: OptionType;
  consentType: string;
  sessionReviewedDate: DateString;
  participated: boolean;
  agreeDisagree: string;
  exceptionList: string;
  employeeSignature: {
    id: string;
    isUpdated: boolean;
    signature: string;
    verbalConsent: boolean;
    date: DateString;
    partitionKey: string;
    rowKey: string;
  };
  parentSignature: {
    id: string;
    isUpdated: boolean;
    signature: string;
    verbalConsent: boolean;
    date: DateString;
    partitionKey: string;
    rowKey: string;
  }[];
  consent: boolean;
  consentSignature: {
    id: string;
    isUpdated: boolean;
    signature: string;
    verbalConsent: boolean;
    date: DateString;
    partitionKey: string;
    rowKey: string;
  }[];
}

const ConsentChangeServiceDeliveryPlan = ({
  close,
  handelClose,
  closeInfo,
  info,
  openInfo,
  isIfspScreenLocked,
  canEditAfterLock,
  selectedLanguage,
  hasEditPermission,
}: ConsentChangeServiceDeliveryPlanProps) => {
  const { ifsp, updateIfspContext } = useContext(IfspContext);

  const [consentForChangeforSDP, setConsentForChangeforSDP] = useState<
    ConsentChangeServiceDeliveryPlanDataType[]
  >(ifsp.ifspTeam.forms.consentForChangeForSDP);
  const [openAccordionIndex, setOpenAccordionIndex] = useState<number>(0);
  const [openAccordian, setOpenAccordian] = useState<boolean>(false);
  const rctl = useIntl();
  const permission = hasEditPermission && selectedLanguage === 'en';
  const [signClicked, setSignClicked] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');

  useEffect(() => {
    if (!permission && !canEditAfterLock) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          e.preventDefault();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [permission, canEditAfterLock]);

  useEffect(() => {
    if (close) setOpenAccordian(false);
  }, [close]);
  const parentRef = useRef<HTMLDivElement>(null);

  const handleParentSignature = (
    updatedSignatures: Signature[],
    index: number,
  ) => {
    const signatureDataURI = updatedSignatures[index]?.signature;

    setConsentForChangeforSDP((prev) => {
      return prev.map((item, i) => {
        if (i === openAccordionIndex) {
          const updatedParentSignatures = [...item.parentSignature];
          updatedParentSignatures[index] = {
            id: 'p.' + index.toString(),
            isUpdated: true,
            signature: signatureDataURI,
            verbalConsent: updatedSignatures[index]?.verbalConsent,
            date: updatedSignatures[index]?.date,
            partitionKey: updatedSignatures[index]?.partitionKey,
            rowKey: updatedSignatures[index]?.rowKey,
          };

          return {
            ...item,
            parentSignature: updatedParentSignatures,
          };
        }
        return item;
      });
    });
  };

  const handleConsentSignature = (
    updatedSignatures: Signature[],
    index: number,
  ) => {
    const signatureDataURI = updatedSignatures[index]?.signature;

    setConsentForChangeforSDP((prev) => {
      return prev.map((item, i) => {
        if (i === openAccordionIndex) {
          const updatedConsentSignatures = [...item.consentSignature];
          updatedConsentSignatures[index] = {
            id: 'c.' + index.toString(),
            isUpdated: true,
            signature: signatureDataURI,
            verbalConsent: updatedSignatures[index]?.verbalConsent,
            date: updatedSignatures[index]?.date,
            partitionKey: updatedSignatures[index]?.partitionKey,
            rowKey: updatedSignatures[index]?.rowKey,
          };

          return {
            ...item,
            consentSignature: updatedConsentSignatures,
          };
        }
        return item;
      });
    });
  };

  useEffect(() => {
    const newConsentForChangeForSDP =
      ifsp.ifspTeam.forms.consentForChangeForSDP;
    if (newConsentForChangeForSDP !== consentForChangeforSDP) {
      setConsentForChangeforSDP(newConsentForChangeForSDP);
    }
  }, [ifsp]);

  useEffect(() => {
    updateIfspContext({
      ...ifsp,
      ifspTeam: {
        ...ifsp.ifspTeam,
        forms: {
          ...ifsp.ifspTeam.forms,
          consentForChangeForSDP: consentForChangeforSDP,
        },
      },
    });
  }, [consentForChangeforSDP]);

  const showSessionReviewedDate = (date: string | Dayjs | null) => {
    return (
      <Box
        sx={{
          display: 'flex',
          padding: '4px 8px 4px 8px',
          borderRadius: '4px',
          marginLeft: '24px',
          backgroundColor: '#97A6A5',
          alignItems: 'center',
        }}
      >
        <MediumTypography
          labelid="IFSP.CompletedOn"
          defaultlabel="Completed On: "
          fontSize="14px"
          fontweight={400}
          textColor="#fff"
        />
        <MediumTypography
          fontSize="14px"
          fontweight={400}
          textColor="#fff"
          className="pl-xs"
          label={date as string}
        />
      </Box>
    );
  };

  const showAddSectionCreationUi = (mainIndex: number) => {
    return !isIfspScreenLocked &&
      permission &&
      mainIndex === ifsp.ifspTeam.forms.consentForChangeForSDP.length - 1 &&
      consentForChangeforSDP[mainIndex]?.consentType === 'consent' &&
      !shouldForceDisableOldIfsp() ? (
      <Box
        component={'div'}
        className="cursorPointer flex__ align__items__center pr-md"
        onClick={() => {
          setConsentForChangeforSDP((prev) => {
            return prev.map((item, index) => {
              if (index === mainIndex) {
                return {
                  ...item,
                  consentType: 'consentForChange',
                };
              }
              return item;
            });
          });
        }}
        sx={{
          padding: '4px 8px 4px 8px',
          borderRadius: '4px',
          marginRight: '24px',
          border: '1px solid #008C82',
          backgroundColor: '#fff',
          pointerEvents: hasEditPermission ? 'auto' : 'none',
        }}
      >
        <Box sx={{ display: 'flex' }} className="pr-xs">
          {mainIndex > 0 && <UnDo style={{ width: '14px', height: '14px' }} />}
        </Box>
        <MediumTypography
          labelid={
            mainIndex === 0 ? 'IFSP.ConvertToConsentForChange' : 'IFSP.UnDo'
          }
          defaultlabel={
            mainIndex === 0 ? 'Convert To Consent For Change' : 'Undo'
          }
          fontSize="12px"
          fontweight={600}
        />
      </Box>
    ) : (
      !isIfspScreenLocked &&
        permission &&
        mainIndex === ifsp.ifspTeam.forms.consentForChangeForSDP.length - 1 &&
        consentForChangeforSDP[mainIndex]?.consentType === 'consentForChange' &&
        !shouldForceDisableOldIfsp() && (
          <Box
            className="cursorPointer flex__ align__items__center pr-md"
            sx={{
              padding: '4px 8px 4px 8px',
              borderRadius: '4px',
              marginRight: '24px',
              border: '1px solid #008C82',
              backgroundColor: '#fff',
              pointerEvents: hasEditPermission ? 'auto' : 'none',
            }}
            onClick={() => {
              setConsentForChangeforSDP((prev) => {
                return prev.map((item, index) => {
                  if (index === mainIndex) {
                    return {
                      ...item,
                      consentType: 'consent',
                      isConsentSkipped: false,
                    };
                  }
                  return item;
                });
              });
            }}
          >
            <Box sx={{ display: 'flex' }} className="pr-xs">
              {mainIndex === 0 && (
                <UnDo style={{ width: '14px', height: '14px' }} />
              )}
            </Box>
            <MediumTypography
              labelid={mainIndex === 0 ? 'IFSP.UnDo' : 'IFSP.ConvertToConsent'}
              defaultlabel={mainIndex === 0 ? 'Undo' : 'Convert To Consent'}
              fontSize="12px"
              fontweight={600}
            />
          </Box>
        )
    );
  };

  const labelValue = (
    mainIndex: number,
    consentTypeValue: string,
    consentForChnageTypeValye: string,
  ) => {
    if (consentForChangeforSDP[mainIndex]?.isAdminAmendment) {
      return 'Admin Amendment';
    } else if (
      consentForChangeforSDP[mainIndex]?.consentType === 'consentForChange'
    ) {
      return consentForChnageTypeValye;
    } else {
      return consentTypeValue;
    }
  };

  const onCancelClick = () => {
    setSignClicked(!signClicked);
  };

  const onSignatureSave = async (sign: string) => {
    const userId = localStorage.getItem('userId');
    setSignClicked(!signClicked);
    setConsentForChangeforSDP((prev) => {
      return prev.map((item, index) => {
        if (index === openAccordionIndex) {
          return {
            ...item,
            employeeSignature: {
              ...item.employeeSignature,
              id: userId ?? '',
              partitionKey: '',
              rowKey: '',
              signature: sign,
            },
          };
        } else {
          return item;
        }
      });
    });
  };

  const signatureCallBack = (response: SignatureStateType[]) => {
    setConsentForChangeforSDP((prev) => {
      return prev.map((item, index) => {
        if (index === openAccordionIndex) {
          return {
            ...item,
            employeeSignature: {
              ...item.employeeSignature,
              partitionKey: response[0].partitionkey,
              rowKey: response[0].rowkey,
              signature: response[0].signaturedata,
            },
          };
        } else {
          return item;
        }
      });
    });
  };

  const clearSignature = () => {
    setConsentForChangeforSDP((prev) => {
      return prev.map((item, index) => {
        if (index === openAccordionIndex) {
          return {
            ...item,
            employeeSignature: {
              ...item.employeeSignature,
              partitionKey: '',
              rowKey: '',
              signature: '',
            },
          };
        } else {
          return item;
        }
      });
    });
    setOpen(true);
    setToastrVariable('success');
    setToastrId('ProgressNote.signatureResetedsuccess');
    setToastrDefaultMessage('Signature Reset Successfully');
    setSignClicked(!signClicked);
  };
  const handleSuccess = (successError: AlertColor, id: string, mes: string) => {
    setOpen(true);
    setToastrVariable(successError);
    setToastrId(id);
    setToastrDefaultMessage(mes);
  };

  const handleCloseSnakBar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  return (
    <Box component="main">
      <SnackBarComponent
        open={open}
        handleClose={handleCloseSnakBar}
        successOrError={toastrVariable}
        labelId={toastrId}
        defaultMessageId={toastrDefaultMessage}
      />
      {info && (
        <InformationModal
          open={info}
          titleLabel="IFSP.Information"
          titleDefaultLabel="Information"
          buttonLabel="IFSP.InfoOkButton"
          buttonDefaultLabel="Ok"
          onClose={() => {
            closeInfo(info);
          }}
          info={
            ifsp.ifspTeam.forms.consentForChangeForSDP[openAccordionIndex]
              ?.consentType === 'consentForChange'
              ? "A review of the Individualized Family Service Plan (IFSP) for a child and the child's family must be conducted every six (6) months or more frequently if conditions warrant or if the family requests a meeting to review the IFSP. The purpose of the periodic review is to determine the degree to which progress toward achieving the results or outcomes identified in the IFSP is being made and/or if modifications or revisions of the results, outcomes or Early Intervention services identified in the IFSP are necessary. The review may be carried out by a meeting or by another means that is acceptable to parents and other participants."
              : 'Early Intervention is a voluntary service and parent(s) have the right to provide, withhold or revoke consent for any and all services. Parents must give written consent before services can begin. Parents may choose to give consent for some services and not others. Your consent means that you agree to the services outlined in this IFSP. If the parents do not give consent for an Early Intervention Service or if they withdraw consent after first giving it, that specific service will not be provided. Any service for which a parent provides consent must be provided within 30 days.'
          }
        />
      )}
      {ifsp.ifspTeam.forms.consentForChangeForSDP.map((ifspData, mainIndex) => (
        <Box
          key={`${mainIndex}-${ifspData.sessionReviewedDate}`}
          component="section"
          className="p-lg mt-lg borderRadius4"
          sx={{
            border: '1px solid #00C6B8',
            backgroundColor: '#ECF9F8',
          }}
        >
          <Box component="div" className="flex__justify__space-between">
            {openAccordian && mainIndex === openAccordionIndex ? (
              <Box component="div" className="flex__justify__space-between">
                <MediumTypography
                  labelid={labelValue(
                    mainIndex,
                    'IFSP.session9',
                    'IFSP.ConsentForChanges',
                  )}
                  defaultlabel={labelValue(
                    mainIndex,
                    'Cnsent',
                    'Consent For Changes',
                  )}
                  fontSize="16px"
                  fontweight={600}
                  textColor="#2A4241"
                  className="pr-md"
                />
                <InfoIcon
                  className="cursorPointer"
                  onClick={() => {
                    openInfo(info);
                  }}
                />
                {ifsp.ifspTeam.forms.consentForChangeForSDP[mainIndex]
                  .sessionReviewedDate &&
                  showSessionReviewedDate(
                    ifsp.ifspTeam.forms.consentForChangeForSDP[mainIndex]
                      .sessionReviewedDate,
                  )}
              </Box>
            ) : (
              <Box className="flex__justify__space-between">
                <MediumTypography
                  labelid={labelValue(
                    mainIndex,
                    'IFSP.session9',
                    'IFSP.ConsentForChanges',
                  )}
                  defaultlabel={labelValue(
                    mainIndex,
                    'Cnsent',
                    'Consent For Changes',
                  )}
                  fontSize="18px"
                  fontweight={600}
                  textColor="#2A4241"
                />
                {ifsp.ifspTeam.forms.consentForChangeForSDP[mainIndex]
                  .sessionReviewedDate &&
                  showSessionReviewedDate(
                    ifsp.ifspTeam.forms.consentForChangeForSDP[mainIndex]
                      .sessionReviewedDate,
                  )}
              </Box>
            )}
            {openAccordian && mainIndex === openAccordionIndex ? (
              <Box className="flex__ align__items__center">
                {
                  // Case 1: New record (last index)
                  // (ifsp.ifspTeam.forms.consentForChangeForSDP.length === 1 &&
                  // ifsp.ifspTeam.forms.consentForChangeForSDP[mainIndex]
                  //   .consentType === 'consentForChange') ||
                  ((ifsp.ifspTeam.forms.consentForChangeForSDP.length >= 1 &&
                    ifsp.ifspTeam.forms.consentForChangeForSDP.length - 1 &&
                    ifsp.ifspTeam.forms.consentForChangeForSDP[mainIndex]
                      .consentType === 'consentForChange' &&
                    !ifsp.ifspTeam.forms.consentForChangeForSDP[mainIndex]
                      .sessionReviewedDate) ||
                    (ifsp.ifspTeam.forms.consentForChangeForSDP[mainIndex]
                      .sessionReviewedDate &&
                      !ifsp.ifspTeam.forms.consentForChangeForSDP[mainIndex]
                        .isAdminAmendment &&
                      ifsp.ifspTeam.forms.consentForChangeForSDP[mainIndex]
                        .isConsentSkipped &&
                      ifsp.ifspTeam.forms.consentForChangeForSDP[mainIndex]
                        .consentType === 'consentForChange' &&
                      canEditAfterLock)) &&
                    !shouldForceDisableOldIfsp() && (
                      <Box
                        component={'div'}
                        className="cursorPointer flex__ align__items__center pr-md"
                        onClick={() => {
                          setConsentForChangeforSDP((prev) => {
                            return prev.map((item, index) => {
                              if (index === mainIndex) {
                                return {
                                  ...item,
                                  changes: '',
                                  isConsentGiven: false,
                                  isAdminAmendment: !item.isAdminAmendment,
                                  isConsentSkipped: false,
                                  isEligibleUntilThirdBirthday: '',
                                  eligibleTool: { id: '', label: '' },
                                  isEligibleFor: '',
                                  assessmentCompletionDate: null,
                                  reEstablishedDate: null,
                                  parentInitial: { id: '', label: '' },
                                  sessionReviewedDate: null,
                                  participated: false,
                                  agreeDisagree: '',
                                  exceptionList: '',
                                  employeeSignature: {
                                    id: '',
                                    isUpdated: false,
                                    signature: '',
                                    verbalConsent: false,
                                    date: null,
                                    partitionKey: '',
                                    rowKey: '',
                                  },
                                  parentSignature: [
                                    {
                                      id: 'p.0',
                                      isUpdated: false,
                                      signature: '',
                                      verbalConsent: false,
                                      date: null,
                                      partitionKey: '',
                                      rowKey: '',
                                    },
                                    {
                                      id: 'p.1',
                                      isUpdated: false,
                                      signature: '',
                                      verbalConsent: false,
                                      date: null,
                                      partitionKey: '',
                                      rowKey: '',
                                    },
                                  ],
                                  consent: false,
                                  consentSignature: [
                                    {
                                      id: 'c.0',
                                      isUpdated: false,
                                      signature: '',
                                      verbalConsent: false,
                                      date: null,
                                      partitionKey: '',
                                      rowKey: '',
                                    },
                                    {
                                      id: 'c.1',
                                      isUpdated: false,
                                      signature: '',
                                      verbalConsent: false,
                                      date: null,
                                      partitionKey: '',
                                      rowKey: '',
                                    },
                                  ],
                                };
                              }
                              return item;
                            });
                          });
                        }}
                        sx={{
                          padding: '4px 8px 4px 8px',
                          borderRadius: '4px',
                          marginRight: '24px',
                          border: '1px solid #008C82',
                          backgroundColor: '#fff',
                        }}
                      >
                        <Box sx={{ display: 'flex' }} className="pr-xs">
                          {consentForChangeforSDP[mainIndex]
                            ?.isAdminAmendment && (
                            <UnDo style={{ width: '14px', height: '14px' }} />
                          )}
                        </Box>
                        <MediumTypography
                          labelid={'IFSP.ConvertToAdminAmendment'}
                          defaultlabel={
                            consentForChangeforSDP[mainIndex]?.isAdminAmendment
                              ? 'Undo'
                              : 'Convert to Admin Amendment'
                          }
                          fontSize="12px"
                          fontweight={600}
                        />
                      </Box>
                    )
                }
                {!consentForChangeforSDP[mainIndex]?.isAdminAmendment &&
                  showAddSectionCreationUi(mainIndex)}
                <UpArrow
                  className="cursorPointer"
                  onClick={() => {
                    setOpenAccordionIndex((prev) => prev);
                    setOpenAccordian(false);
                  }}
                />
              </Box>
            ) : (
              <Box className="flex__ align__items__center">
                {((ifsp.ifspTeam.forms.consentForChangeForSDP.length >= 1 &&
                  ifsp.ifspTeam.forms.consentForChangeForSDP.length - 1 &&
                  ifsp.ifspTeam.forms.consentForChangeForSDP[mainIndex]
                    .consentType === 'consentForChange' &&
                  !ifsp.ifspTeam.forms.consentForChangeForSDP[mainIndex]
                    .sessionReviewedDate) ||
                  (ifsp.ifspTeam.forms.consentForChangeForSDP[mainIndex]
                    .sessionReviewedDate &&
                    !ifsp.ifspTeam.forms.consentForChangeForSDP[mainIndex]
                      .isAdminAmendment &&
                    ifsp.ifspTeam.forms.consentForChangeForSDP[mainIndex]
                      .isConsentSkipped &&
                    ifsp.ifspTeam.forms.consentForChangeForSDP[mainIndex]
                      .consentType === 'consentForChange' &&
                    canEditAfterLock)) &&
                  !shouldForceDisableOldIfsp() && (
                    <Box
                      component={'div'}
                      className="cursorPointer flex__ align__items__center pr-md"
                      onClick={() => {
                        setConsentForChangeforSDP((prev) => {
                          return prev.map((item, index) => {
                            if (index === mainIndex) {
                              return {
                                ...item,
                                changes: '',
                                isConsentGiven: false,
                                isAdminAmendment: !item.isAdminAmendment,
                                isConsentSkipped: false,
                                isEligibleUntilThirdBirthday: '',
                                eligibleTool: { id: '', label: '' },
                                isEligibleFor: '',
                                assessmentCompletionDate: null,
                                reEstablishedDate: null,
                                parentInitial: { id: '', label: '' },
                                sessionReviewedDate: null,
                                participated: false,
                                agreeDisagree: '',
                                exceptionList: '',
                                employeeSignature: {
                                  id: '',
                                  isUpdated: false,
                                  signature: '',
                                  verbalConsent: false,
                                  date: null,
                                  partitionKey: '',
                                  rowKey: '',
                                },
                                parentSignature: [
                                  {
                                    id: 'p.0',
                                    isUpdated: false,
                                    signature: '',
                                    verbalConsent: false,
                                    date: null,
                                    partitionKey: '',
                                    rowKey: '',
                                  },
                                  {
                                    id: 'p.1',
                                    isUpdated: false,
                                    signature: '',
                                    verbalConsent: false,
                                    date: null,
                                    partitionKey: '',
                                    rowKey: '',
                                  },
                                ],
                                consent: false,
                                consentSignature: [
                                  {
                                    id: 'c.0',
                                    isUpdated: false,
                                    signature: '',
                                    verbalConsent: false,
                                    date: null,
                                    partitionKey: '',
                                    rowKey: '',
                                  },
                                  {
                                    id: 'c.1',
                                    isUpdated: false,
                                    signature: '',
                                    verbalConsent: false,
                                    date: null,
                                    partitionKey: '',
                                    rowKey: '',
                                  },
                                ],
                              };
                            }
                            return item;
                          });
                        });
                      }}
                      sx={{
                        padding: '4px 8px 4px 8px',
                        borderRadius: '4px',
                        marginRight: '24px',
                        border: '1px solid #008C82',
                        backgroundColor: '#fff',
                      }}
                    >
                      <Box sx={{ display: 'flex' }} className="pr-xs">
                        {consentForChangeforSDP[mainIndex]
                          ?.isAdminAmendment && (
                          <UnDo style={{ width: '14px', height: '14px' }} />
                        )}
                      </Box>
                      <MediumTypography
                        labelid={'IFSP.ConvertToAdminAmendment'}
                        defaultlabel={
                          consentForChangeforSDP[mainIndex]?.isAdminAmendment
                            ? 'Undo'
                            : 'Convert to Admin Amendment'
                        }
                        fontSize="12px"
                        fontweight={600}
                      />
                    </Box>
                  )}
                {!consentForChangeforSDP[mainIndex]?.isAdminAmendment &&
                  showAddSectionCreationUi(mainIndex)}
                <DownArrow
                  className="cursorPointer"
                  onClick={() => {
                    setOpenAccordionIndex(mainIndex);
                    setOpenAccordian(true);
                    handelClose(false);
                  }}
                />
              </Box>
            )}
          </Box>
          {openAccordian && mainIndex === openAccordionIndex && (
            <Box component="div">
              <Box
                ref={parentRef}
                style={{
                  pointerEvents:
                    !permission && !canEditAfterLock ? 'none' : 'auto',
                  opacity: !permission && !canEditAfterLock ? '0.8' : 'unset',
                }}
              >
                {(consentForChangeforSDP[mainIndex]?.consentType ===
                  'consentForChange' &&
                  consentForChangeforSDP[mainIndex].sessionReviewedDate &&
                  consentForChangeforSDP[mainIndex].isConsentSkipped) ||
                consentForChangeforSDP[mainIndex].isAdminAmendment ? (
                  <Box className="mt-md">
                    <CheckBoxComponent
                      ischecked={
                        consentForChangeforSDP[openAccordionIndex]
                          .isConsentSkipped
                      }
                      CheckHandleChange={(value) =>
                        setConsentForChangeforSDP((prev) => {
                          return prev.map((item, index) => {
                            if (index === openAccordionIndex) {
                              return {
                                ...item,
                                changes: '',
                                isConsentGiven: false,
                                isAdminAmendment: true,
                                isConsentSkipped: value.target.checked,
                                isEligibleUntilThirdBirthday: '',
                                eligibleTool: { id: '', label: '' },
                                isEligibleFor: '',
                                assessmentCompletionDate: null,
                                reEstablishedDate: null,
                                parentInitial: { id: '', label: '' },

                                sessionReviewedDate: null,
                                participated: false,
                                agreeDisagree: '',
                                exceptionList: '',
                                employeeSignature: {
                                  id: '',
                                  isUpdated: false,
                                  signature: '',
                                  verbalConsent: false,
                                  date: null,
                                  partitionKey: '',
                                  rowKey: '',
                                },
                                parentSignature: [
                                  {
                                    id: 'p.0',
                                    isUpdated: false,
                                    signature: '',
                                    verbalConsent: false,
                                    date: null,
                                    partitionKey: '',
                                    rowKey: '',
                                  },
                                  {
                                    id: 'p.1',
                                    isUpdated: false,
                                    signature: '',
                                    verbalConsent: false,
                                    date: null,
                                    partitionKey: '',
                                    rowKey: '',
                                  },
                                ],
                                consent: false,
                                consentSignature: [
                                  {
                                    id: 'c.0',
                                    isUpdated: false,
                                    signature: '',
                                    verbalConsent: false,
                                    date: null,
                                    partitionKey: '',
                                    rowKey: '',
                                  },
                                  {
                                    id: 'c.1',
                                    isUpdated: false,
                                    signature: '',
                                    verbalConsent: false,
                                    date: null,
                                    partitionKey: '',
                                    rowKey: '',
                                  },
                                ],
                              };
                            }
                            return item;
                          });
                        })
                      }
                      labelid="IFSP.skipToConsent"
                      defaultlabelid="Service Coordinator or administrative attestation of outcome and service information amendments for data correction or discharge purposes only. Parent/Guardian consent is not required, or signature is on file."
                      disabled={!!ifspData.sessionReviewedDate}
                    />
                  </Box>
                ) : null}
                {(consentForChangeforSDP[mainIndex]?.isAdminAmendment ||
                  consentForChangeforSDP[mainIndex]?.consentType ===
                    'consentForChange') && (
                  <>
                    <MediumTypography
                      labelid="IFSP.changesToServiceDelivery"
                      defaultlabel="Changes To Service Delivery"
                      fontSize="16px"
                      fontweight={600}
                      textColor="#2A4241"
                      className="pt-md pb-sm"
                    />
                    <CommentTextbox
                      className="bg__white translate"
                      placeholder={rctl.formatMessage({
                        id: 'IFSP.enterYourComments',
                        defaultMessage: 'Enter your comments',
                      })}
                      style={{
                        width: '840px',
                        textAlign: 'justify',
                      }}
                      rows={2}
                      maxLength={3000}
                      Value={consentForChangeforSDP[openAccordionIndex].changes}
                      handlechange={(text) =>
                        setConsentForChangeforSDP((prev) => {
                          return prev.map((item, index) => {
                            if (index === openAccordionIndex) {
                              return {
                                ...item,
                                changes: text,
                              };
                            } else {
                              return item;
                            }
                          });
                        })
                      }
                      disabled={
                        (!!ifspData.sessionReviewedDate && !canEditAfterLock) ||
                        (!ifspData.isConsentSkipped &&
                          ifspData.isAdminAmendment) ||
                        (ifspData.isConsentSkipped &&
                          !ifspData.isAdminAmendment)
                      }
                    />
                  </>
                )}
                {consentForChangeforSDP[mainIndex]?.isAdminAmendment ? (
                  <Box>
                    {consentForChangeforSDP[openAccordionIndex]?.changes ===
                      '' &&
                      consentForChangeforSDP[openAccordionIndex]
                        .isConsentSkipped && (
                        <MediumTypography
                          labelid="IFSP.DateRed"
                          defaultlabel="Changes To Service Delivery is required"
                          textColor="red"
                        />
                      )}
                    <MediumTypography
                      labelid="sd"
                      defaultlabel="Signature"
                      fontSize="16px"
                      fontweight={600}
                      textColor="#2A4241"
                      className="pt-md pb-sm"
                    />
                    <Box sx={{ paddingBottom: '24px' }}>
                      {consentForChangeforSDP[openAccordionIndex]
                        ?.employeeSignature?.signature === '' && (
                        <Box className="flex__ align__items__center">
                          <Box>
                            <ButtonComponent
                              className="btn-primary btn-cancel signature_add_Box"
                              labelId="Insurance.Addsignature"
                              defaultLabelId="Add Signature"
                              variantType="outlined"
                              onClick={() => setSignClicked(true)}
                              disabled={
                                (!!ifspData.sessionReviewedDate &&
                                  !canEditAfterLock) ||
                                !ifspData.isConsentSkipped
                              }
                            />
                            {consentForChangeforSDP[openAccordionIndex]
                              ?.employeeSignature?.signature === '' &&
                              consentForChangeforSDP[openAccordionIndex]
                                .isConsentSkipped && (
                                <MediumTypography
                                  labelid="IFSP.DateRed"
                                  defaultlabel="Signature is required"
                                  textColor="red"
                                  className="pt-sm"
                                />
                              )}
                          </Box>
                          <Box sx={{ width: '200px', paddingLeft: '16px' }}>
                            <DatePickerComponent
                              className="bg__white"
                              labelid={'Date'}
                              defaultlabelid={'Date'}
                              value={dayjs(
                                consentForChangeforSDP[openAccordionIndex]
                                  .employeeSignature?.date,
                              )}
                              handlechange={(dateValue) => {
                                if (dateValue) {
                                  const formattedDate =
                                    dayjs(dateValue).format('MM/DD/YYYY');
                                  setConsentForChangeforSDP((prev) => {
                                    return prev.map((item, index) => {
                                      if (index === openAccordionIndex) {
                                        return {
                                          ...item,
                                          employeeSignature: {
                                            ...item.employeeSignature,
                                            date: formattedDate,
                                          },
                                        };
                                      } else {
                                        return item;
                                      }
                                    });
                                  });
                                } else {
                                  setConsentForChangeforSDP((prev) => {
                                    return prev.map((item, index) => {
                                      if (index === openAccordionIndex) {
                                        return {
                                          ...item,
                                          employeeSignature: {
                                            ...item.employeeSignature,
                                            date: null,
                                          },
                                        };
                                      } else {
                                        return item;
                                      }
                                    });
                                  });
                                }
                              }}
                              disabledDate={
                                (!!ifspData.sessionReviewedDate &&
                                  !canEditAfterLock) ||
                                !consentForChangeforSDP[openAccordionIndex]
                                  .employeeSignature?.signature
                              }
                            />
                            {!consentForChangeforSDP[openAccordionIndex]
                              .employeeSignature?.date &&
                              consentForChangeforSDP[openAccordionIndex]
                                .employeeSignature?.signature && (
                                <MediumTypography
                                  labelid="IFSP.DateRed"
                                  defaultlabel="Date is required"
                                  textColor="red"
                                />
                              )}
                          </Box>
                        </Box>
                      )}

                      {consentForChangeforSDP[openAccordionIndex]
                        ?.employeeSignature?.signature !== '' && (
                        <Box className="flex__ align__items__center">
                          <ButtonComponent
                            className="btn-primary btn-cancel signature_view_Box"
                            labelId={'ProgressNoteViewSignature'}
                            defaultLabelId={'View signature'}
                            variantType="outlined"
                            LeftIcon={<Editsignature />}
                            onClick={() => setSignClicked(true)}
                          />
                          <Box sx={{ width: '200px', paddingLeft: '16px' }}>
                            <DatePickerComponent
                              className="bg__white"
                              labelid={'Date'}
                              defaultlabelid={'Date'}
                              value={dayjs(
                                consentForChangeforSDP[openAccordionIndex]
                                  .employeeSignature?.date,
                              )}
                              handlechange={(dateValue) => {
                                if (dateValue) {
                                  const formattedDate =
                                    dayjs(dateValue).format('MM/DD/YYYY');
                                  setConsentForChangeforSDP((prev) => {
                                    return prev.map((item, index) => {
                                      if (index === openAccordionIndex) {
                                        return {
                                          ...item,
                                          employeeSignature: {
                                            ...item.employeeSignature,
                                            date: formattedDate,
                                          },
                                        };
                                      } else {
                                        return item;
                                      }
                                    });
                                  });
                                } else {
                                  setConsentForChangeforSDP((prev) => {
                                    return prev.map((item, index) => {
                                      if (index === openAccordionIndex) {
                                        return {
                                          ...item,
                                          employeeSignature: {
                                            ...item.employeeSignature,
                                            date: null,
                                          },
                                        };
                                      } else {
                                        return item;
                                      }
                                    });
                                  });
                                }
                              }}
                              disabledDate={
                                (!!ifspData.sessionReviewedDate &&
                                  !canEditAfterLock) ||
                                !ifspData.isConsentSkipped
                              }
                            />
                            {!consentForChangeforSDP[openAccordionIndex]
                              .employeeSignature?.date &&
                              consentForChangeforSDP[openAccordionIndex]
                                .employeeSignature?.signature && (
                                <MediumTypography
                                  labelid="IFSP.DateRed"
                                  defaultlabel="Date is required"
                                  textColor="red"
                                />
                              )}
                          </Box>
                        </Box>
                      )}
                    </Box>

                    {signClicked && (
                      <SignatureComponent
                        signClicked={signClicked}
                        labelId="signatureText"
                        defaultLabelId="Signature"
                        onCancelClick={onCancelClick}
                        signature={
                          consentForChangeforSDP[openAccordionIndex]
                            .employeeSignature.signature ?? ''
                        }
                        signaturePartitionKey={
                          consentForChangeforSDP[openAccordionIndex]
                            .employeeSignature.partitionKey ?? ''
                        }
                        signatureRowKey={
                          consentForChangeforSDP[openAccordionIndex]
                            .employeeSignature.rowKey ?? ''
                        }
                        signatureSaveOrUPdate={onSignatureSave}
                        resetCallBackForTypeEmployee={clearSignature}
                        signatureCallBack={signatureCallBack}
                        handleSuccess={handleSuccess}
                        type="Employee"
                        hideRemoveButton={
                          !!ifspData.sessionReviewedDate && !canEditAfterLock
                        }
                      />
                    )}
                  </Box>
                ) : null}
                {consentForChangeforSDP[mainIndex]?.consentType === 'consent' &&
                !consentForChangeforSDP[mainIndex]?.isAdminAmendment ? (
                  <Box>
                    <MediumTypography
                      labelid="IFSP.Eligibility"
                      defaultlabel="Eligibility"
                      fontSize="16px"
                      fontweight={600}
                      textColor="#2A4241"
                      className="pt-md pb-md"
                    />
                    <MediumTypography
                      labelid="IFSP.ifspServicesProvidedForAsLongAs"
                      defaultlabel="IFSP services are provided for as long as a child is eligible or until the parent revokes consent for any or all services."
                      fontweight={400}
                      textColor="#2A4241"
                    />
                    <Box component="div" className="pl-md pt-sm">
                      <CustomRadioButton
                        data={[
                          {
                            value: 'iseligible',
                            labelId: 'IFSP.IsEligible',
                            defaultLabel:
                              'Is eligible based on the eligibility evaluation and assessment completed',
                          },
                          {
                            value: 'untilnotonthirdbirthday',
                            labelId: 'IFSP.UntilButNotOnOurChildsThirdBirthday',
                            defaultLabel:
                              "Until (but not on) our child's third birthday based on a diagnosis from the DPH Diagnosed Conditions List.",
                          },
                        ]}
                        value={
                          consentForChangeforSDP[openAccordionIndex]
                            .isEligibleUntilThirdBirthday
                        }
                        onSelectItem={(value) => {
                          if (value) {
                            if (value === 'untilnotonthirdbirthday') {
                              setConsentForChangeforSDP((prev) => {
                                return prev.map((item, index) => {
                                  if (index === openAccordionIndex) {
                                    return {
                                      ...item,
                                      assessmentCompletionDate: null,
                                      isEligibleUntilThirdBirthday: value,
                                      eligibleTool: {
                                        id: '',
                                        label: '',
                                      },
                                      isEligibleFor: '',
                                    };
                                  }
                                  return item;
                                });
                              });
                            } else {
                              setConsentForChangeforSDP((prev) => {
                                return prev.map((item, index) => {
                                  if (index === openAccordionIndex) {
                                    return {
                                      ...item,
                                      assessmentCompletionDate:
                                        item.assessmentCompletionDate ||
                                        ifsp.evaluationDate ||
                                        null,
                                      reEstablishedDate:
                                        item.reEstablishedDate ||
                                        (ifsp.notificationDate &&
                                          dayjs(
                                            ifsp.notificationDate,
                                            'MM/DD/YYYY',
                                          )
                                            .add(364, 'days')
                                            .format('MM/DD/YYYY')) ||
                                        null,
                                      isEligibleUntilThirdBirthday: value,
                                    };
                                  }
                                  return item;
                                });
                              });
                            }
                          }
                        }}
                        row={false}
                        flexDirection="column"
                        paddingY="4px"
                        disable={
                          (!!ifspData.sessionReviewedDate &&
                            !canEditAfterLock) ||
                          consentForChangeforSDP[openAccordionIndex]
                            .isConsentSkipped
                        }
                      />
                    </Box>
                    {consentForChangeforSDP[openAccordionIndex]
                      .isEligibleUntilThirdBirthday === 'iseligible' && (
                      <Box component="div">
                        <Grid container className="alignItemCenter pt-sm">
                          <Grid item>
                            <MediumTypography
                              labelid="IFSP.EligibilityEvaluation"
                              defaultlabel="Eligibility evaluation:"
                              className="pr-md"
                              fontweight={400}
                              textColor="#2A4241"
                            />
                          </Grid>
                          <Grid item>
                            <AutocompleteSearch
                              labelId={'IFSP.Tool'}
                              defaultId={'Tool'}
                              optionHintId={'noemployeeDropdownTextId'}
                              sxProps={{
                                [`& .${autocompleteClasses.popupIndicator}`]: {
                                  transform: 'none',
                                },
                                backgroundColor: '#fff',
                                width: '220px',
                              }}
                              selectedValue={
                                consentForChangeforSDP[openAccordionIndex]
                                  .eligibleTool
                              }
                              data={[{ id: 'bdi2', label: 'BDI 3' }]}
                              onSelectItem={(value: OptionType | null) => {
                                if (value) {
                                  setConsentForChangeforSDP((prev) => {
                                    return prev.map((item, index) => {
                                      if (index === openAccordionIndex) {
                                        return {
                                          ...item,
                                          eligibleTool: {
                                            id: value.id,
                                            label: value.label,
                                          },
                                        };
                                      }
                                      return item;
                                    });
                                  });
                                }
                              }}
                              disableLabel={false}
                              disabled={
                                (!!ifspData.sessionReviewedDate &&
                                  !canEditAfterLock) ||
                                consentForChangeforSDP[openAccordionIndex]
                                  .isConsentSkipped
                              }
                            />
                          </Grid>
                          <Grid item>
                            <MediumTypography
                              labelid="IFSP.IsEligibleFor"
                              defaultlabel="Is Eligible for:"
                              className="pr-md"
                              fontweight={400}
                              textColor="#2A4241"
                            />
                            <CommentTextbox
                              className="bg__white translate"
                              placeholder={rctl.formatMessage({
                                id: 'IFSP.enterYourComments',
                                defaultMessage: 'Enter your comments',
                              })}
                              style={{
                                width: '840px',
                                textAlign: 'justify',
                              }}
                              rows={2}
                              maxLength={3000}
                              Value={
                                consentForChangeforSDP[openAccordionIndex]
                                  .isEligibleFor
                              }
                              handlechange={(text) =>
                                setConsentForChangeforSDP((prev) => {
                                  return prev.map((item, index) => {
                                    if (index === openAccordionIndex) {
                                      return {
                                        ...item,
                                        isEligibleFor: text,
                                      };
                                    } else {
                                      return item;
                                    }
                                  });
                                })
                              }
                              disabled={
                                (!!ifspData.sessionReviewedDate &&
                                  !canEditAfterLock) ||
                                consentForChangeforSDP[openAccordionIndex]
                                  .isConsentSkipped
                              }
                            />
                          </Grid>
                        </Grid>
                        <Grid container className="alignItemCenter pt-sm">
                          <Grid item>
                            <MediumTypography
                              labelid="IFSP.AssessmentCompletedOn"
                              defaultlabel="Assessment completed on:"
                              className="pr-md"
                              fontweight={400}
                              textColor="#2A4241"
                            />
                          </Grid>
                          <Grid item>
                            <DatePickerComponent
                              className="bg__white"
                              labelid={'Date'}
                              defaultlabelid={'Date'}
                              value={dayjs(
                                consentForChangeforSDP[openAccordionIndex]
                                  .assessmentCompletionDate,
                              )}
                              handlechange={(dateValue: Dayjs | null) => {
                                if (dateValue) {
                                  const formattedDate =
                                    dayjs(dateValue).format('MM/DD/YYYY');
                                  setConsentForChangeforSDP((prev) => {
                                    return prev.map((item, index) => {
                                      if (index === openAccordionIndex) {
                                        return {
                                          ...item,
                                          assessmentCompletionDate:
                                            formattedDate,
                                        };
                                      } else {
                                        return item;
                                      }
                                    });
                                  });
                                } else {
                                  setConsentForChangeforSDP((prev) => {
                                    return prev.map((item, index) => {
                                      if (index === openAccordionIndex) {
                                        return {
                                          ...item,
                                          assessmentCompletionDate: null,
                                        };
                                      } else {
                                        return item;
                                      }
                                    });
                                  });
                                }
                              }}
                              disabledDate={
                                (!!ifspData.sessionReviewedDate &&
                                  !canEditAfterLock) ||
                                consentForChangeforSDP[openAccordionIndex]
                                  .isConsentSkipped
                              }
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                    <Grid container className="alignItemCenter pt-sm">
                      <Grid item>
                        <MediumTypography
                          labelid="IFSP.EligibilityMustBeReestablishedOn"
                          defaultlabel="Eligibility must be re-established on:"
                          className="pr-md"
                          fontweight={400}
                          textColor="#2A4241"
                        />
                      </Grid>
                      <Grid item>
                        <DatePickerComponent
                          className="bg__white"
                          labelid={'Date'}
                          defaultlabelid={'Date'}
                          value={dayjs(
                            consentForChangeforSDP[openAccordionIndex]
                              .reEstablishedDate,
                          )}
                          handlechange={(dateValue: Dayjs | null) => {
                            if (dateValue) {
                              const formattedDate =
                                dayjs(dateValue).format('MM/DD/YYYY');
                              setConsentForChangeforSDP((prev) => {
                                return prev.map((item, index) => {
                                  if (index === openAccordionIndex) {
                                    return {
                                      ...item,
                                      reEstablishedDate: formattedDate,
                                    };
                                  }
                                  return item;
                                });
                              });
                            } else {
                              setConsentForChangeforSDP((prev) => {
                                return prev.map((item, index) => {
                                  if (index === openAccordionIndex) {
                                    return {
                                      ...item,
                                      reEstablishedDate: null,
                                    };
                                  }
                                  return item;
                                });
                              });
                            }
                          }}
                          disabledDate={
                            (!!ifspData.sessionReviewedDate &&
                              !canEditAfterLock) ||
                            consentForChangeforSDP[openAccordionIndex]
                              .isConsentSkipped
                          }
                        />
                      </Grid>
                    </Grid>
                    <MediumTypography
                      labelid="IFSP.servicesMayOccurForLessThan"
                      defaultlabel="*Services may occurforlessthan the specified period based on the child's age atthe time of the eligibility evaluation. All EIservices end one day prior to the child'sthird birthday."
                      className="pt-sm"
                      fontweight={400}
                      textColor="#2A4241"
                    />
                  </Box>
                ) : null}
                {!consentForChangeforSDP[mainIndex]?.isAdminAmendment && (
                  <>
                    <MediumTypography
                      labelid="IFSP.familyRightsNotice"
                      defaultlabel="Family Rights Notice"
                      fontSize="16px"
                      fontweight={600}
                      textColor="#2A4241"
                      className="pt-md pb-md"
                    />
                    <AutocompleteSearch
                      labelId={'IFSP.familyRightsNotice'}
                      defaultId="Family Rights Notice"
                      optionHintId={'noemployeeDropdownTextId'}
                      sxProps={{
                        [`& .${autocompleteClasses.popupIndicator}`]: {
                          transform: 'none',
                        },
                        backgroundColor: '#fff',
                        width: '840px',
                      }}
                      data={ifsp.ifspTeam.contact.map((item) => ({
                        id: item.id.toString(),
                        label: item.name ? item.name ?? '' : '',
                      }))}
                      onSelectItem={(value: OptionType | null) => {
                        if (value) {
                          setConsentForChangeforSDP((prev) => {
                            return prev.map((item, index) => {
                              if (index === openAccordionIndex) {
                                return {
                                  ...item,
                                  parentInitial: {
                                    id: value.id,
                                    label: value.label,
                                  },
                                };
                              }
                              return item;
                            });
                          });
                        }
                      }}
                      selectedValue={
                        consentForChangeforSDP[openAccordionIndex].parentInitial
                      }
                      disableLabel={false}
                      disabled={
                        (!!ifspData.sessionReviewedDate && !canEditAfterLock) ||
                        consentForChangeforSDP[openAccordionIndex]
                          .isConsentSkipped
                      }
                    />
                    <MediumTypography
                      labelid="IFSP.understandingTheConsent"
                      defaultlabel="I/We have been informed of and received a copy of our Family Rights during the IFSP development process and understand that any accepted services will be provided."
                      fontweight={400}
                      textColor="#2A4241"
                      className="pt-lg"
                    />
                    <MediumTypography
                      labelid="IFSP.consentForService"
                      defaultlabel="Consent For Service"
                      fontSize="16px"
                      fontweight={600}
                      textColor="#2A4241"
                      className="pt-md"
                    />
                  </>
                )}
                {consentForChangeforSDP[mainIndex]?.consentType ===
                  'consentForChange' &&
                !consentForChangeforSDP[mainIndex]?.isAdminAmendment ? (
                  <Box>
                    <MediumTypography
                      labelid="IFSP.parentRights"
                      defaultlabel="Parent have the right to provide, withhold or revoke consent for any and all services. Parent must give written consent for any change in EI services. Consent means that you have been made aware of changes and that you agree to them. Any service for which a parent provides consent must be provided within 30 days."
                      fontweight={400}
                      textColor="#2A4241"
                      className="pt-sm"
                    />
                  </Box>
                ) : null}
                {consentForChangeforSDP[mainIndex]?.isAdminAmendment ? null : (
                  <>
                    <Box
                      component="div"
                      className="flex__ align__items__center pb-xs pt-md"
                    >
                      <CheckBoxComponent
                        ischecked={
                          consentForChangeforSDP[openAccordionIndex]
                            .participated
                        }
                        CheckHandleChange={(value) =>
                          setConsentForChangeforSDP((prev) => {
                            return prev.map((item, index) => {
                              if (index === openAccordionIndex) {
                                return {
                                  ...item,
                                  participated: value.target.checked,
                                };
                              }
                              return item;
                            });
                          })
                        }
                        className="pl-sm"
                        disabled={
                          !!ifspData.sessionReviewedDate ||
                          consentForChangeforSDP[openAccordionIndex]
                            .isConsentSkipped
                        }
                        labelid="IFSP.participatedIFSP"
                        defaultlabelid="I/We have participated in the development of our IFSP and:"
                      />
                    </Box>
                    <Box component="div" className="pl-lg">
                      <CustomRadioButton
                        data={[
                          {
                            value: 'agree',
                            labelId: 'IFSP.agree',
                            defaultLabel:
                              'I/We agree to to the services described in this plan.',
                          },
                          {
                            value: 'disagree',
                            labelId: 'IFSP.disagree',
                            defaultLabel:
                              'I/We decline IFSP services at this time',
                          },
                          {
                            value: 'agreeException',
                            labelId: 'IFSP.agreeException',
                            defaultLabel:
                              'I/We agree to the services in this plan with the following exceptions',
                          },
                        ]}
                        value={
                          consentForChangeforSDP[openAccordionIndex]
                            .agreeDisagree
                        }
                        onSelectItem={(value) => {
                          if (value) {
                            setConsentForChangeforSDP((prev) => {
                              return prev.map((item, index) => {
                                if (index === openAccordionIndex) {
                                  return { ...item, agreeDisagree: value };
                                }
                                return item;
                              });
                            });
                          }
                        }}
                        row={false}
                        flexDirection="column"
                        paddingY="4px"
                        disable={
                          (!!ifspData.sessionReviewedDate &&
                            !canEditAfterLock) ||
                          consentForChangeforSDP[openAccordionIndex]
                            .isConsentSkipped
                        }
                      />
                    </Box>
                    <MediumTypography
                      labelid="IFSP.exceptionList"
                      defaultlabel="List exceptions"
                      fontSize="16px"
                      fontweight={600}
                      textColor="#2A4241"
                      className="pt-md"
                    />
                    <CommentTextbox
                      className="bg__white translate"
                      placeholder={rctl.formatMessage({
                        id: 'IFSP.enterYourComments',
                        defaultMessage: 'Enter your comments',
                      })}
                      style={{
                        width: '840px',
                        textAlign: 'justify',
                      }}
                      rows={2}
                      maxLength={500}
                      Value={
                        consentForChangeforSDP[openAccordionIndex].exceptionList
                      }
                      handlechange={(text) =>
                        setConsentForChangeforSDP((prev) => {
                          return prev.map((item, index) => {
                            if (index === openAccordionIndex) {
                              return { ...item, exceptionList: text };
                            }
                            return item;
                          });
                        })
                      }
                      disabled={
                        (!!ifspData.sessionReviewedDate && !canEditAfterLock) ||
                        consentForChangeforSDP[openAccordionIndex]
                          .isConsentSkipped
                      }
                    />
                    <IfspSignature
                      openAccordianIndex={openAccordionIndex}
                      signatureNeed="parent"
                      consentType={
                        consentForChangeforSDP[openAccordionIndex].consentType
                      }
                      onSignatureChange={handleParentSignature}
                      idIndication="p."
                      canEditAfterLock={canEditAfterLock}
                      disabled={
                        !!ifspData.sessionReviewedDate ||
                        !consentForChangeforSDP[openAccordionIndex]
                          .participated ||
                        consentForChangeforSDP[openAccordionIndex]
                          .isConsentSkipped
                      }
                    />
                    <MediumTypography
                      labelid="IFSP.consentToAccessInsurance"
                      defaultlabel="Consent To Access Insurance"
                      fontSize="16px"
                      fontweight={600}
                      textColor="#2A4241"
                      className="pt-md"
                    />
                    <Box
                      component="div"
                      className="flex__ align__items__center"
                    >
                      <Box component="div" className="pt-xs">
                        <CheckBoxComponent
                          ischecked={
                            consentForChangeforSDP[openAccordionIndex].consent
                          }
                          CheckHandleChange={(value) =>
                            setConsentForChangeforSDP((prev) => {
                              return prev.map((item, index) => {
                                if (index === openAccordionIndex) {
                                  return {
                                    ...item,
                                    consent: value.target.checked,
                                  };
                                }
                                return item;
                              });
                            })
                          }
                          disabled={
                            !!ifspData.sessionReviewedDate ||
                            consentForChangeforSDP[openAccordionIndex]
                              .isConsentSkipped
                          }
                          className="pl-sm"
                          labelid="IFSP.InsuranceContent"
                          defaultlabelid=" I/We consent for the EI Program to access our public and/ or private insurance for payment of Early Intervention services described in this plan."
                        />
                      </Box>
                    </Box>
                    <IfspSignature
                      openAccordianIndex={openAccordionIndex}
                      signatureNeed="consent"
                      consentType={
                        consentForChangeforSDP[openAccordionIndex].consentType
                      }
                      onSignatureChange={handleConsentSignature}
                      idIndication="c."
                      canEditAfterLock={canEditAfterLock}
                      disabled={
                        !!ifspData.sessionReviewedDate ||
                        !consentForChangeforSDP[openAccordionIndex].consent ||
                        consentForChangeforSDP[openAccordionIndex]
                          .isConsentSkipped
                      }
                    />
                  </>
                )}
              </Box>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default ConsentChangeServiceDeliveryPlan;
